import { SeoHead } from "@/components/SeoHead";
import { useState, useMemo, useRef, useEffect, type ReactNode } from "react";
import { ChevronDown, Search, X } from "lucide-react";
import { ALL_ITEMS, FAQ_SECTIONS, faqSchema } from "../data/faqContent";

function highlight(text: string, query: string) {
  if (!query.trim()) return <>{text}</>;
  const regex = new RegExp(`(${query.trim().replace(/[.*+?^${}()|[\]\\]/g, "\\$&")})`, "gi");
  const parts = text.split(regex);
  return (
    <>
      {parts.map((part, i) =>
        regex.test(part) ? (
          <mark key={i} className="bg-yellow-200 text-yellow-900 rounded px-0.5">
            {part}
          </mark>
        ) : (
          part
        )
      )}
    </>
  );
}

function FaqItem({
  q,
  a,
  forceOpen = false,
  query = "",
}: {
  q: string;
  a: ReactNode;
  forceOpen?: boolean;
  query?: string;
}) {
  const [open, setOpen] = useState(false);
  const isOpen = forceOpen || open;
  const contentRef = useRef<HTMLDivElement>(null);
  const [height, setHeight] = useState<string>("0px");

  useEffect(() => {
    if (isOpen && contentRef.current) {
      setHeight(contentRef.current.scrollHeight + "px");
    } else {
      setHeight("0px");
    }
  }, [isOpen]);

  return (
    <div
      className={`rounded-2xl border transition-all duration-200 overflow-hidden bg-card
        ${isOpen
          ? "border-primary/30 shadow-md"
          : "border-border/60 shadow-sm hover:shadow-md hover:border-border"
        }`}
      style={isOpen ? { borderLeftWidth: "3px", borderLeftColor: "hsl(var(--primary))" } : {}}
    >
      <button
        className="w-full flex items-center justify-between px-5 py-4 text-left gap-4 group"
        onClick={() => setOpen((v) => !v)}
      >
        <span
          className={`font-medium text-sm leading-relaxed transition-colors duration-150
            ${isOpen ? "text-primary" : "text-foreground group-hover:text-primary"}`}
        >
          {query ? highlight(q, query) : q}
        </span>
        <span
          className={`shrink-0 h-7 w-7 rounded-full flex items-center justify-center transition-all duration-200
            ${isOpen
              ? "bg-primary text-primary-foreground"
              : "bg-muted text-muted-foreground group-hover:bg-primary/10 group-hover:text-primary"
            }`}
        >
          <ChevronDown
            className={`h-4 w-4 transition-transform duration-300 ${isOpen ? "rotate-180" : ""}`}
          />
        </span>
      </button>

      {/* Smooth height animation */}
      <div style={{ maxHeight: height, transition: "max-height 0.3s ease", overflow: "hidden" }}>
        <div
          ref={contentRef}
          className="px-5 pb-5 pt-4 text-sm leading-relaxed text-muted-foreground space-y-2 border-t border-dashed border-border/50"
        >
          {a}
        </div>
      </div>
    </div>
  );
}

export default function FaqPage() {
  const [query, setQuery] = useState("");

  const trimmed = query.trim().toLowerCase();

  const filtered = useMemo(() => {
    if (!trimmed) return null; // null = show all sections normally
    return ALL_ITEMS.filter(
      (item) =>
        item.q.toLowerCase().includes(trimmed) ||
        item.keywords.toLowerCase().includes(trimmed)
    );
  }, [trimmed]);

  return (
    <div className="min-h-screen bg-background">
      <SeoHead
        title="常見問題 FAQ — 香港學校招標平台 skoltend.com"
        description="解答供應商及學校關於香港學校招標採購的常見問題：公開招標金額界線、書面報價要求、承辦商名冊登記、投標資格文件等，一站式解答。"
        canonical="/faq"
        structuredData={[faqSchema(filtered ?? ALL_ITEMS)]}
      />
      {/* Hero */}
      <div className="border-b bg-secondary/30 py-8">
        <div className="container mx-auto px-4 md:px-8 max-w-3xl">
          <p className="text-xs font-medium text-primary uppercase tracking-widest mb-2">常見問題</p>
          <h1 className="text-2xl md:text-3xl font-bold mb-2 leading-snug">
            常見問題 (FAQ)
          </h1>
          <p className="text-muted-foreground text-sm leading-relaxed max-w-2xl">
            收集供應商及學校行政人員最常詢問的學校招標採購問題，提供直接明確的答案。
          </p>
        </div>
      </div>
      {/* Content */}
      <div className="container mx-auto px-4 md:px-8 py-10 max-w-3xl space-y-10">

        {/* ── Search bar ── */}
        <div className="relative">
          <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="輸入關鍵字搜尋，例如「書面報價」、「IT 招標」、「會員費用」…"
            className="w-full pl-10 pr-10 py-3 text-sm rounded-xl border bg-background shadow-sm focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary transition-all placeholder:text-muted-foreground/60"
          />
          {query && (
            <button
              onClick={() => setQuery("")}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
              aria-label="清除搜尋"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>

        {/* ── Search results ── */}
        {filtered !== null ? (
          filtered.length === 0 ? (
            <div className="py-16 text-center space-y-2">
              <p className="text-muted-foreground text-sm">找不到與「<strong className="text-foreground">{query}</strong>」相關的問題。</p>
              <p className="text-xs text-muted-foreground/70">請嘗試其他關鍵字，或<button onClick={() => setQuery("")} className="underline underline-offset-2 hover:text-primary transition-colors">瀏覽全部問題</button>。</p>
            </div>
          ) : (
            <div className="space-y-3">
              <p className="text-xs text-muted-foreground">
                找到 <strong className="text-foreground">{filtered.length}</strong> 條相關結果
              </p>
              {filtered.map(({ q, a }) => (
                <FaqItem key={q} q={q} a={a} forceOpen query={trimmed} />
              ))}
            </div>
          )
        ) : (
          /* ── Normal grouped view ── */
          (<div className="space-y-12">
            {FAQ_SECTIONS.map(({ section, items }) => (
              <section key={section} className="space-y-4">
                <div className="flex items-center gap-3">
                  <span className="bg-primary/10 text-primary font-semibold px-3 py-1 rounded-full whitespace-nowrap text-[20px]">
                    {section}
                  </span>
                  <div className="flex-1 h-px bg-border" />
                </div>
                <div className="space-y-3">
                  {items.map(({ q, a }) => (
                    <FaqItem key={q} q={q} a={a} />
                  ))}
                </div>
              </section>
            ))}
          </div>)
        )}

        {/* CTA */}
        <div className="rounded-xl bg-primary/5 border border-primary/20 p-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div className="space-y-1">
            <p className="font-semibold text-sm">仍有疑問？</p>
            <p className="text-muted-foreground text-xs">歡迎透過聯絡頁面向我們查詢，我們會盡快回覆。</p>
          </div>
          <a href="/contact">
            <button className="shrink-0 bg-primary text-primary-foreground text-sm font-medium px-5 py-2.5 rounded-lg hover:opacity-90 transition-opacity">
              聯絡我們 →
            </button>
          </a>
        </div>
      </div>
    </div>
  );
}
